/**
 * PII / sensitive-data scrubbing for log entries.
 *
 * Defence: deny-by-default for patterns we know about; never log raw bodies
 * or query strings in production without explicit allowlisting at the call site.
 *
 * Patterns covered:
 *  - Email addresses
 *  - Card PANs (12–19 digits, Luhn-valid)
 *  - Bearer tokens in strings
 *
 * Intentional non-goals:
 *  - Names / addresses — too false-positive-prone for regex; rely on call-site discipline.
 */
const EMAIL_RE = /([A-Za-z0-9._%+-]+)@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
const PAN_RE = /\b\d(?:[ -]?\d){11,18}\b/g;
const BEARER_RE = /(bearer\s+)[\w.-]+/gi;

function isLuhnValid(digits: string): boolean {
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    const ch = digits.charCodeAt(i);
    if (ch < 48 || ch > 57) return false;
    let n = ch - 48;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum > 0 && sum % 10 === 0;
}

function scrubString(value: string): string {
  let out = value.replace(EMAIL_RE, (_match, local: string, domain: string) => {
    const head = local.length > 0 ? local[0] : '';
    return `${head ?? ''}***@${domain}`;
  });
  out = out.replace(PAN_RE, (match) => {
    const digits = match.replace(/[ -]/g, '');
    if (digits.length < 12 || digits.length > 19) return match;
    if (!isLuhnValid(digits)) return match;
    const last4 = digits.slice(-4);
    return `**** **** **** ${last4}`;
  });
  out = out.replace(BEARER_RE, '$1[redacted]');
  return out;
}

function scrubValue(value: unknown, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') return scrubString(value);
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, seen));
  if (value && typeof value === 'object') {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = scrubValue(v, seen);
    }
    return out;
  }
  return value;
}

export function scrub<T>(value: T): T {
  return scrubValue(value, new WeakSet()) as T;
}
